import React, { useMemo, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * @typedef {{ value: string|number, label: any, disabled?: boolean }} DropdownOption
 */

const Dropdown = ({
  className = "",
  variant = "select",
  id,
  ariaLabel,
  placeholderKey,
  options = [],
  value,
  onChange,
  multiple = false,
  disabled = false,
  maxWidth = 412,
  chipLimit = 3,
  selectedPluralKey = "dropdown.selected_plural",
  sx = {},
  autocompleteProps = {},
}) => {
  const { t } = useTranslation();

  const selectRef = useRef(null);
  const [menuWidth, setMenuWidth] = useState(undefined);

  const typographyBodySm = {
    fontSize: "14px",
    fontWeight: 400,
    letterSpacing: "0.28px",
    lineHeight: "24px",
  };

  const borderDropdownColor = "rgba(46, 64, 77, 1)";
  const backgroundDropdown = "rgba(255, 255, 255, 1)";
  const selectTextColor = "rgba(7, 15, 38, 1)";
  const chipTextColor = "rgba(255, 255, 255, 1)";
  const chipColor = "var(--main-color)";

  const commonInputSx = {
    ...typographyBodySm,
    width: "100%",
    backgroundColor: disabled ? "#e8e8e8" : backgroundDropdown,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: disabled ? "rgba(0, 0, 0, 0.2)" : borderDropdownColor,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: borderDropdownColor,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: borderDropdownColor,
    },
    pointerEvents: disabled ? "none" : "auto",
    maxWidth,
    ...sx,
  };

  const ITEM_HEIGHT = 44;
  const MENU_MAX_ITEMS = 5;

  const collator = useMemo(
    () => new Intl.Collator(undefined, { sensitivity: "base", numeric: true }),
    [],
  );

  const alphabetizedOptions = useMemo(() => {
    return [...options].sort((a, b) =>
      collator.compare(String(a?.label ?? ""), String(b?.label ?? "")),
    );
  }, [options, collator]);

  const optionValuesSet = useMemo(
    () => new Set(alphabetizedOptions.map((o) => o.value)),
    [alphabetizedOptions],
  );

  const normalizedValue = useMemo(() => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      return arr.filter((v) => optionValuesSet.has(v));
    }

    return optionValuesSet.has(value) ? value : "";
  }, [value, multiple, optionValuesSet]);

  const selectedOptions = useMemo(() => {
    if (variant !== "autocomplete") return multiple ? [] : null;

    if (!multiple) {
      return (
        alphabetizedOptions.find((o) => o.value === normalizedValue) || null
      );
    }

    const arr = Array.isArray(normalizedValue) ? normalizedValue : [];
    return alphabetizedOptions.filter((o) => arr.includes(o.value));
  }, [variant, multiple, alphabetizedOptions, normalizedValue]);

  const sortedOptions = useMemo(() => {
    if (variant !== "autocomplete" || !multiple) return alphabetizedOptions;

    const selectedSet = new Set(
      (Array.isArray(normalizedValue) ? normalizedValue : []).map(String),
    );

    const sel = alphabetizedOptions.filter((o) =>
      selectedSet.has(String(o.value)),
    );

    const rest = alphabetizedOptions.filter(
      (o) => !selectedSet.has(String(o.value)),
    );

    return [...sel, ...rest];
  }, [variant, multiple, alphabetizedOptions, normalizedValue]);

  const renderSelectedForSelect = (selected) => {
    if (multiple) {
      const selectedArr = Array.isArray(selected) ? selected : [];
      if (selectedArr.length === 0) return t(placeholderKey);

      const labels = selectedArr
        .map(
          (v) =>
            alphabetizedOptions.find((o) => o.value === v)?.label ?? String(v),
        )
        .filter(Boolean);

      return labels.join(", ");
    }

    if (!selected) return t(placeholderKey);

    return (
      alphabetizedOptions.find((o) => o.value === selected)?.label ??
      String(selected)
    );
  };

  return (
    <Box className={`dropdownButtons ${className}`} sx={{ width: "100%" }}>
      {variant === "select" ? (
        <FormControl
          size="small"
          sx={{
            width: "100%",
            maxWidth,
          }}
        >
          <Select
            ref={selectRef}
            id={id}
            inputProps={
              ariaLabel ? { "aria-label": ariaLabel } : undefined
            }
            SelectDisplayProps={
              ariaLabel ? { "aria-label": ariaLabel } : undefined
            }
            value={normalizedValue}
            multiple={multiple}
            onOpen={() => {
              setMenuWidth(selectRef.current?.clientWidth);
            }}
            onChange={(e) => onChange(e.target.value)}
            displayEmpty
            renderValue={renderSelectedForSelect}
            IconComponent={KeyboardArrowDownIcon}
            disabled={disabled}
            sx={{
              ...commonInputSx,
              color: disabled ? "rgba(0, 0, 0, 0.38)" : selectTextColor,
              height: 44,
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  width: menuWidth,
                  maxWidth: menuWidth,
                  maxHeight: ITEM_HEIGHT * MENU_MAX_ITEMS,
                  overflowY: "auto",
                  overflowX: "hidden",
                },
              },
            }}
          >
            {!multiple && (
              <MenuItem value="">
                <em>{t(placeholderKey)}</em>
              </MenuItem>
            )}

            {alphabetizedOptions.map((opt) => (
              <MenuItem
                key={String(opt.value)}
                value={opt.value}
                disabled={Boolean(opt.disabled)}
                sx={{
                  width: "100%",
                  maxWidth: menuWidth,
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Autocomplete
          multiple={multiple}
          options={sortedOptions}
          disableCloseOnSelect={multiple}
          sx={{ maxWidth, width: "100%" }}
          value={selectedOptions}
          disabled={disabled}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          getOptionLabel={(opt) => (opt?.label ? String(opt.label) : "")}
          onChange={(event, newValue) => {
            if (!multiple) {
              onChange(newValue?.value ?? "");
              return;
            }

            onChange(
              Array.isArray(newValue) ? newValue.map((i) => i.value) : [],
            );
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={String(option.value)}>
              {multiple && (
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
              )}
              <ListItemText primary={option.label} />
            </li>
          )}
          renderTags={(selected, getTagProps) => {
            if (!multiple) return null;

            if (selected.length > 0 && selected.length <= chipLimit) {
              return selected.map((opt, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={String(opt.value)}
                  label={opt.label}
                  sx={{
                    ...typographyBodySm,
                    color: chipTextColor,
                    backgroundColor: chipColor,
                    borderColor: borderDropdownColor,
                    "& .MuiChip-deleteIcon": { color: "white" },
                    "& .MuiChip-deleteIcon:hover": { color: "white" },
                  }}
                />
              ));
            }

            if (selected.length > chipLimit) {
              const tooltipTitle = selected.map((s) => s.label).join(", ");

              return (
                <Tooltip title={tooltipTitle} arrow>
                  <Chip
                    label={t(selectedPluralKey, { count: selected.length })}
                    sx={{
                      ...typographyBodySm,
                      color: chipTextColor,
                      backgroundColor: chipColor,
                      borderColor: borderDropdownColor,
                    }}
                  />
                </Tooltip>
              );
            }

            return null;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={t(placeholderKey)}
              size="small"
              inputProps={{
                ...params.inputProps,
                ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
              }}
              sx={commonInputSx}
            />
          )}
          {...autocompleteProps}
        />
      )}
    </Box>
  );
};

export default Dropdown;
